import React, { useState, useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  BarChart3,
  DollarSign,
  FileText,
  Calendar,
  Loader2,
  RefreshCw,
  Gauge,
  ChevronLeft,
  ChevronRight,
  Shield,
  ArrowUpRight,
  Zap,
  CreditCard,
  ChevronDown,
  Trash2,
  Star,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { PortalApiError } from "@/lib/portalApi";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { overageRateClause } from "@/components/portal/apps/reporting/custom/entitlements";
import { usePermissions } from "@/hooks/usePermissions";
import {
  billingApi,
  isStoredInvoice,
  meteredSplit,
  OrganisationUsageResponse,
  UsageWithLimitsResponse,
  PerServiceUsageLimitsResponse,
  ServiceUsageLimits,
  CustomerInvoiceApiResponse,
  BillingHistoryRecord,
  MarginAnalysisResponse,
  BillingHistoryResponse,
  PaymentMethodRecord,
} from "@/services/api/billing";
import { platformApi } from "@/services/api/platform";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  countryLabel,
  resolveVatTreatment,
  vatPricingHint,
  vatTreatmentLabel,
} from "@/lib/countries";
import type { VatTreatment } from "@/lib/countries";
import { useCountries } from "@/hooks/useCountries";
import { initialBillingTab } from "@/lib/billingTab";
import { formatCalendarDay, formatCalendarMonth } from "@/lib/dateFormat";
import { invoiceVoidAvailability, voidReasonError } from "@/lib/invoiceVoid";
import { SERVICE_NAMES } from "@/lib/serviceNames";
import { CustomerInvoiceBreakdown } from "@/components/portal/platform/CustomerInvoiceBreakdown";
import { StoredInvoiceLineItems } from "@/components/portal/platform/StoredInvoiceLineItems";

/** Human wording for a VAT treatment, used in the admin explainer lines. */
function treatmentLabel(treatment: VatTreatment): string {
  switch (treatment) {
    case "STANDARD":
      return "standard rated";
    case "ZERO_RATED_EXPORT":
      return "a zero-rated export";
    case "EXEMPT":
      return "exempt";
    default:
      return treatment;
  }
}

export const Route = createFileRoute("/portal/platform/billing")({
  component: UsageBillingPage,
});

const METRIC_LABELS: Record<string, { label: string; unit: string }> = {
  rag_queries: { label: "File Queries", unit: "queries" },
  pages_ingested: { label: "Pages Ingested", unit: "pages" },
  // Landing AI pages parsed by workflow document extraction (CP metric
  // ("document_extraction", "document_extract_pages"): its own product, billed
  // per page, not part of rag's "File Queries").
  document_extract_pages: { label: "Document extraction (pages)", unit: "pages" },
  storage_gib: { label: "Storage", unit: "GiB" },
  pages_rewrite: { label: "pages rewrite", unit: "pages" },
  pages_generate: { label: "pages generate", unit: "pages" },
  sql_rag_queries: { label: "Queries", unit: "queries" },
  reports: { label: "Custom Reports", unit: "reports" },
  // Without these the overage clause falls back to "per unit over" for
  // metrics the CP does price (measured on lsza00 2026-09-15 via
  // mcp__prod-internal__query over cp01.ServiceTier.OveragePricingByCountry:
  // messaging emails_sent, workflows nodes, wireguard connections).
  emails_sent: { label: "Emails Sent", unit: "emails" },
  whatsapp_utility_msgs: { label: "WhatsApp Utility Messages", unit: "messages" },
  whatsapp_marketing_msgs: { label: "WhatsApp Marketing Messages", unit: "messages" },
  nodes: { label: "Workflow Nodes", unit: "nodes" },
  connections: { label: "VPN Connections", unit: "connections" },
};

// The UTC calendar month, EXACTLY as the billing scheduler defines it.
//
// `billing_scheduler.py:1393-1394` computes the invoiced period as
// `first-of-next-month 00:00Z minus one second` .. `first-of-this-month 00:00Z`,
// so July 2026 was invoiced over [Jul 1 00:00:00Z, Jul 31 23:59:59Z]. Anything
// else here means the portal's "July" cannot reproduce the invoice it is meant
// to be showing — which is the entire point of the month picker.
//
// WHAT THIS REPLACES: `new Date(y, m + 1, 0)` is 00:00 LOCAL on the last day of
// the month, not the end of it, and both bounds were local-midnight serialised
// through `.toISOString()`. For a SAST (UTC+2) viewer "July" arrived at the API
// as [Jun 30 22:00Z, Jul 30 22:00Z): usage handlers filter `>= start AND < end`
// (subscription.py:2158-2161), so the last ~26 hours of July were silently
// dropped, 2 hours of June were counted, and stepping months left a full day
// belonging to neither.
//
// The one-second gap at the end of the month is the scheduler's, and is
// REPRODUCED DELIBERATELY. Closing it here — by sending the exclusive start of
// the next month — would make the portal disagree with the issued invoice,
// which is the failure this function exists to remove, not a fix. If that gap
// is ever closed it has to be closed in the scheduler first.
// WHICH month is read in UTC too, not just its bounds. `getFullYear()` and
// `getMonth()` are LOCAL calendar fields, so building UTC bounds out of them
// mixes two calendars: for a SAST viewer at 00:30 local on the 1st, the local
// month has ticked over but UTC has not, and the default "current month" was a
// window starting 1.5 hours in the FUTURE — every figure on the page zero until
// 02:00. The picker's own label is already formatted with `timeZone: "UTC"`, so
// the label and the query disagreed for those two hours.
/**
 * "R x subscription + R y usage + R z overages" under the month's total —
 * storage and per-unit usage are not overages (cp invoice-usage-lines), so
 * the summary names them apart and omits a zero part.
 */
function MeteredSummary({
  subscription,
  split,
  formatCurrency,
}: {
  subscription: number;
  split: { usage: number; overages: number };
  formatCurrency: (amount: number) => string;
}) {
  const parts = [`${formatCurrency(subscription)} subscription`];
  if (split.usage > 0) parts.push(`${formatCurrency(split.usage)} usage`);
  if (split.overages > 0) parts.push(`${formatCurrency(split.overages)} overages`);
  return <span data-testid="monthly-bill-split">{parts.join(" + ")}</span>;
}

function getMonthBounds(date: Date): { start: Date; end: Date } {
  const start = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0),
  );
  const end = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 0, 0, 0, 0) - 1000,
  );
  return { start, end };
}

// The first instant of the UTC month containing `date`. Anchoring
// `selectedMonth` here keeps month identity unambiguous: every consumer — the
// bounds, the label, the stepper, the is-current check — reads the same UTC
// month, rather than each deriving one from a local-time instant.
function utcMonthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
}

// Format month for display. `timeZone: "UTC"` is load-bearing for the same
// reason it is on the period label below: `selectedMonth` is the first INSTANT
// of a UTC month, so a viewer behind UTC formatting it locally reads the month
// before — 2026-09-01T00:00Z renders as "August 2026" in New York.
function formatMonth(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function UsageBillingPage() {
  const { token, user } = useAuth();
  const { selectedOrganisation, loading: orgLoading } = usePortalData();
  const { can } = usePermissions();
  const canViewBilling = can("view", "billing");
  // Adding/removing a card hits POST /api/v1/payments/initialize, which the
  // gateway enforces as billing:create. Billing is owner-only, so a non-owner
  // (e.g. a delegate "helping" with card details) is 403'd at the gateway and
  // the request never reaches Paystack. Gate the UI on the same permission so
  // those users don't see a button that can only fail. See task #236.
  const canManageBilling = can("create", "billing");
  const [activeTab, setActiveTab] = useState(() =>
    initialBillingTab(window.location.search),
  );
  const [loading, setLoading] = useState(false);
  const [billingUsage, setBillingUsage] =
    useState<OrganisationUsageResponse | null>(null);
  const [usageLimits, setUsageLimits] =
    useState<UsageWithLimitsResponse | null>(null);
  const [perServiceLimits, setPerServiceLimits] =
    useState<PerServiceUsageLimitsResponse | null>(null);
  const [customerInvoice, setCustomerInvoice] =
    useState<CustomerInvoiceApiResponse | null>(null);
  const [marginAnalysis, setMarginAnalysis] =
    useState<MarginAnalysisResponse | null>(null);
  const [billingHistory, setBillingHistory] =
    useState<BillingHistoryResponse | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRecord[]>(
    [],
  );
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(
    null,
  );
  // The invoice whose charge is in flight, or null.
  //
  // EVERY row's button disables while any charge is running — `disabled` is
  // `payingInvoiceId !== null`, not `=== invoice.id`. That is deliberate: the
  // rows are for one organisation and one card, and a customer who fires two
  // charges at two invoices in the same second learns which one won from a
  // toast. The id (rather than a bare boolean) is what lets the PRESSED row
  // say "Paying…" while the others simply grey out.
  //
  // The previous comment here claimed only the pressed row disables. It did
  // not, and the difference matters to anyone reasoning about a double click.
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);

  // Platform-admin: the correction door (cp #565 item 3).
  //
  // The invoice the Void dialog is open for, or null. A dialog rather than a
  // bare button because the reason is MANDATORY server-side (5–500 chars) and
  // is the entire audit value of the act — a void with no reason is exactly the
  // unanswerable mutation `cp01.InvoiceAuditLog` exists to prevent.
  const [voidTarget, setVoidTarget] = useState<BillingHistoryRecord | null>(
    null,
  );
  const [voidReason, setVoidReason] = useState("");
  const [voidInFlight, setVoidInFlight] = useState(false);

  // Admin: run-billing-now state
  const [triggeringAction, setTriggeringAction] = useState<
    "no_charge" | "charge" | null
  >(null);
  const [triggerResult, setTriggerResult] = useState<
    Awaited<ReturnType<typeof billingApi.adminTriggerBilling>> | null
  >(null);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  // Admin: 3-way billing mode (off / invoice_only / auto_charge)
  const [billingMode, setBillingMode] = useState<
    "off" | "invoice_only" | "auto_charge"
  >("off");
  const [savingBillingMode, setSavingBillingMode] = useState(false);

  // Admin: per-organisation VAT treatment override. NULL is a real, meaningful
  // value — "inherit whatever cp01.Country says for this org's country" — and
  // is NOT the same as "unset". The select models it as the "AUTO" sentinel.
  const [vatTreatment, setVatTreatment] = useState<VatTreatment | null>(null);
  const [orgCountryCode, setOrgCountryCode] = useState<string | null>(null);
  const [savingVatTreatment, setSavingVatTreatment] = useState(false);
  const { options: countryOptions, countries: countryRows } = useCountries();

  // Billing contact (Billing Entity Name + Billing Email + VAT Number)
  const [billingEntityName, setBillingEntityName] = useState<string>("");
  const [billingEmail, setBillingEmail] = useState<string>("");
  // Which Xero contact this org's invoices belong to (item 9). Authoritative
  // over the billing entity name — saving it renames the entity with it.
  const [xeroContactName, setXeroContactName] = useState<string>("");
  const [originalXeroContactName, setOriginalXeroContactName] =
    useState<string>("");
  const [vatNumber, setVatNumber] = useState<string>("");
  const [originalBillingEntityName, setOriginalBillingEntityName] =
    useState<string>("");
  const [originalBillingEmail, setOriginalBillingEmail] = useState<string>("");
  const [originalVatNumber, setOriginalVatNumber] = useState<string>("");
  const [editingBillingContact, setEditingBillingContact] = useState(false);
  const [savingBillingContact, setSavingBillingContact] = useState(false);

  // Billing address — the "Bill To" block synced to the Xero contact.
  const EMPTY_ADDRESS = {
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  };
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [originalAddress, setOriginalAddress] = useState(EMPTY_ADDRESS);

  // Month selector — defaults to the current UTC month, anchored at its first
  // instant so every consumer reads one unambiguous month (see `utcMonthStart`).
  const [selectedMonth, setSelectedMonth] = useState(() => utcMonthStart(new Date()));

  const selectedOrgId = selectedOrganisation?.organisationId;
  const currentOrgName =
    selectedOrganisation?.organisationName || "Organisation";
  const isAdmin = user?.is_admin === true;

  // Get month bounds for display
  const { start: monthStart, end: monthEnd } = getMonthBounds(selectedMonth);

  useEffect(() => {
    if (token && selectedOrgId) {
      loadBillingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrgId, selectedMonth]);

  const loadBillingData = async () => {
    if (!token || !selectedOrgId) return;
    setLoading(true);
    try {
      // Fetch all billing data in parallel
      const [usageRes, limitsRes, perServiceRes, invoiceRes, historyRes] = await Promise.all([
        billingApi.getOrganisationUsage(
          token,
          selectedOrgId,
          monthStart,
          monthEnd,
        ),
        // Deliberately NOT month-scoped: this drives limit enforcement and
        // raises approaching/blocked notifications, so it must always describe
        // the CURRENT position. See the endpoint docstring.
        billingApi.getUsageWithLimits(token, selectedOrgId),
        billingApi.getPerServiceLimits(
          token,
          selectedOrgId,
          monthStart,
          monthEnd,
        ),
        billingApi.getCustomerInvoice(token, selectedOrgId, monthStart, monthEnd),
        billingApi.getBillingHistory(token, selectedOrgId),
      ]);
      setBillingUsage(usageRes);
      setUsageLimits(limitsRes);
      setPerServiceLimits(perServiceRes);
      setCustomerInvoice(invoiceRes);
      setBillingMode(invoiceRes.billing?.billing_mode ?? "off");
      setBillingHistory(historyRes);

      // Fetch payment methods
      try {
        const methodsRes = await billingApi.getPaymentMethods(
          token,
          selectedOrgId,
        );
        setPaymentMethods(methodsRes);
      } catch {
        setPaymentMethods([]);
      }

      // Fetch billing contact (billing_email + vat_number) from org settings
      try {
        const orgSettings = await platformApi.getOrganisationSettings(
          token,
          selectedOrgId,
        );
        const entityName = orgSettings.billing_entity_name ?? "";
        const email = orgSettings.billing_email ?? "";
        const vat = orgSettings.vat_number ?? "";
        const xeroContact = orgSettings.xero_contact_name ?? "";
        setBillingEntityName(entityName);
        setBillingEmail(email);
        setVatNumber(vat);
        setXeroContactName(xeroContact);
        setOriginalBillingEntityName(entityName);
        setOriginalBillingEmail(email);
        setOriginalVatNumber(vat);
        setOriginalXeroContactName(xeroContact);
        const addr = {
          address_line1: orgSettings.address_line1 ?? "",
          address_line2: orgSettings.address_line2 ?? "",
          city: orgSettings.city ?? "",
          state: orgSettings.state ?? "",
          postal_code: orgSettings.postal_code ?? "",
          // Default the address country to the org's billing country (the
          // admin-set country_code that drives currency/VAT) so it stays
          // consistent rather than hand-typed.
          country:
            orgSettings.country ||
            countryLabel(orgSettings.country_code, countryOptions),
        };
        setAddress(addr);
        setOriginalAddress(addr);
        setOrgCountryCode(orgSettings.country_code ?? null);
        setVatTreatment(orgSettings.vat_treatment ?? null);
      } catch {
        // Clear EVERY field this block loads, not just some.
        //
        // The old comment here said "leave whatever the user typed", which is
        // false on the path that matters: after switching organisations these
        // values were LOADED, not typed. Leaving them showed the PREVIOUS org's
        // billing contact, VAT number, address, country and treatment against
        // the new org's name — and Save would then write them to the new org.
        // Losing an in-progress edit to a failed refresh is an annoyance;
        // silently moving one org's VAT number onto another is a money bug.
        setBillingEntityName("");
        setBillingEmail("");
        setVatNumber("");
        setXeroContactName("");
        setOriginalBillingEntityName("");
        setOriginalBillingEmail("");
        setOriginalVatNumber("");
        setOriginalXeroContactName("");
        const emptyAddress = {
          address_line1: "",
          address_line2: "",
          city: "",
          state: "",
          postal_code: "",
          country: "",
        };
        setAddress(emptyAddress);
        setOriginalAddress(emptyAddress);
        setOrgCountryCode(null);
        setVatTreatment(null);
        // Leave edit mode too. Blanking the fields while the card stays
        // editable creates a new hazard: handleSaveBillingContact maps empty
        // strings to null, so a Save from the blanked form would write NULLs
        // over the org's real billing contact and address.
        setEditingBillingContact(false);
      }

      // Fetch margin analysis only for admins
      if (isAdmin) {
        try {
          const marginRes = await billingApi.getMarginAnalysis(
            token,
            selectedOrgId,
            monthStart,
            monthEnd,
          );
          setMarginAnalysis(marginRes);
        } catch {
          setMarginAnalysis(null);
        }
      }
    } catch (error) {
      console.error("Failed to load billing data:", error);
      toast.error("Failed to load billing data");
      setBillingUsage(null);
      setUsageLimits(null);
      setPerServiceLimits(null);
      setCustomerInvoice(null);
      setMarginAnalysis(null);
      setBillingHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    setSelectedMonth(
      (prev) =>
        new Date(Date.UTC(prev.getUTCFullYear(), prev.getUTCMonth() - 1, 1)),
    );
  };

  const goToNextMonth = () => {
    const next = new Date(
      Date.UTC(selectedMonth.getUTCFullYear(), selectedMonth.getUTCMonth() + 1, 1),
    );
    // Don't allow going beyond the current UTC month. Compared against the
    // month START, not `now`: `next <= new Date()` let a viewer step INTO the
    // current month and no further, which is right, but only by accident of
    // both sides being local. Comparing two UTC month starts says what is
    // meant.
    if (next <= utcMonthStart(new Date())) {
      setSelectedMonth(next);
    }
  };

  const isCurrentMonth = () => {
    // UTC on both sides, or a SAST viewer in the first two hours of the month
    // is told they are looking at a past month.
    const now = new Date();
    return (
      selectedMonth.getUTCMonth() === now.getUTCMonth() &&
      selectedMonth.getUTCFullYear() === now.getUTCFullYear()
    );
  };

  // The stored invoice behind the selected month, or null when this month is
  // still being computed live (cp #549).
  //
  // A closed month is answered by the document the customer already holds, so
  // the figures on this page for such a month are not a recalculation and must
  // not be presented as one — hence the "As invoiced" badge below. `null` here
  // covers three cases that all render identically and correctly: an open
  // month, a month with no invoice yet, and a control plane older than wave 2
  // (which sends no `source` at all).
  const storedInvoice: BillingHistoryRecord | null = isStoredInvoice(
    customerInvoice,
  )
    ? customerInvoice.invoice
    : null;

  const billingCurrency = customerInvoice?.billing?.currency || "USD";
  const currencyLocale = billingCurrency === "ZAR" ? "en-ZA" : "en-US";

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(currencyLocale, {
      style: "currency",
      currency: billingCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleSetDefaultMethod = async (methodId: string) => {
    if (!token || !selectedOrgId) return;
    if (!canManageBilling) {
      toast.error(
        "You don't have permission to manage billing for this organisation.",
      );
      return;
    }
    try {
      await billingApi.setDefaultPaymentMethod(token, selectedOrgId, methodId);
      toast.success("Default payment method updated");
      const methods = await billingApi.getPaymentMethods(token, selectedOrgId);
      setPaymentMethods(methods);
    } catch {
      toast.error("Failed to update default payment method");
    }
  };

  const handleRemoveMethod = async (methodId: string) => {
    if (!token || !selectedOrgId) return;
    if (!canManageBilling) {
      toast.error(
        "You don't have permission to manage billing for this organisation.",
      );
      return;
    }
    try {
      await billingApi.removePaymentMethod(token, selectedOrgId, methodId);
      toast.success("Payment method removed");
      const methods = await billingApi.getPaymentMethods(token, selectedOrgId);
      setPaymentMethods(methods);
    } catch {
      toast.error("Failed to remove payment method");
    }
  };

  // Pay an outstanding invoice with the saved card.
  //
  // NOT `handleAddPaymentMethod`, which is what Pay Now used to call: that is
  // the add-a-card flow (a R0.50 tokenisation on a Paystack checkout page), so
  // pressing Pay Now on an overdue invoice took the customer to a checkout for
  // fifty cents and left the invoice overdue. This charges the stored invoice
  // total server-side — no redirect, and the card already on file.
  //
  // The backend's 409 and 402 bodies are written to be read by the customer
  // ("already paid", "the card was declined: <reason>"), so `err.message` —
  // which portalFetch fills from `detail` — is surfaced verbatim rather than
  // replaced with a generic failure string.
  const handlePayInvoice = async (invoiceId: string) => {
    if (!token) return;
    if (!canManageBilling) {
      toast.error(
        "You don't have permission to pay invoices for this organisation. Ask your organisation owner.",
      );
      return;
    }
    setPayingInvoiceId(invoiceId);
    try {
      await billingApi.payInvoice(token, invoiceId);
      toast.success("Payment successful — thank you");
      // NO INNER try/catch HERE, and that is a deliberate non-change.
      //
      // The plan for this wave called for one, on the reasoning that a reload
      // failing after a successful charge would fall into the catch below and
      // tell the customer their PAYMENT had failed. It cannot:
      // `loadBillingData` handles its own errors — every await inside it is
      // wrapped, and its outer catch toasts "Failed to load billing data" —
      // so it never throws, and the branch below is unreachable from here.
      //
      // A wrapper that can never fire is worse than none: it reads as a live
      // guard, so the next person reasoning about post-charge failure modes
      // believes this one is handled. The invariant that actually matters —
      // never show payment-failure copy after a successful charge — is real
      // and is pinned by a Playwright test that aborts the reload.
      await loadBillingData();
    } catch (err) {
      if (err instanceof PortalApiError && err.isPermissionDenied) {
        toast.error(
          "You don't have permission to pay invoices for this organisation. Ask your organisation owner.",
        );
      } else if (err instanceof PortalApiError) {
        toast.error(err.message);
        // A 409 means the invoice moved under us (settled by the retry
        // scheduler, or voided). Re-read so the row stops offering a button
        // that cannot work.
        if (err.status === 409) {
          await loadBillingData();
        }
      } else {
        toast.error("Payment failed — please try again");
      }
    } finally {
      setPayingInvoiceId(null);
    }
  };

  // Void an invoice so its period can be re-billed (platform admin only).
  //
  // The reason bound (5–500) MIRRORS `InvoiceVoidRequest` in cp
  // `src/control_plane/schemas/billing.py:124`. Enforced here as well as there
  // so the operator is told before the round trip, never instead of it — the
  // server's 422 remains the real guard and the submit path below does not
  // assume this check ran.
  //
  // Every backend refusal is a 409 whose `detail` names the remedy (raise a
  // credit note; release the payment claim; re-read because a charge settled),
  // so `err.message` — which portalFetch fills from `detail` — is surfaced
  // verbatim rather than replaced with a generic failure string. A rewritten
  // message here would cost the operator the only instruction they get.
  const handleVoidInvoice = async () => {
    if (!token || !voidTarget) return;
    const reasonError = voidReasonError(voidReason);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    setVoidInFlight(true);
    try {
      await billingApi.voidInvoice(token, voidTarget.id, voidReason.trim());
      toast.success("Invoice voided — the period can be billed again");
      setVoidTarget(null);
      setVoidReason("");
      // `loadBillingData` handles its own errors and never throws, so a reload
      // failure cannot land in the catch below and tell the operator the VOID
      // failed after it succeeded. Same reasoning as `handlePayInvoice`.
      await loadBillingData();
    } catch (err) {
      if (err instanceof PortalApiError) {
        toast.error(err.message);
        // 409 means the row moved under us — settled, already void, or now
        // claimed. Re-read so the table stops offering an action that cannot
        // work, and leave the dialog open so the operator sees the reason
        // against the row it refers to.
        if (err.status === 409) {
          await loadBillingData();
        }
      } else {
        toast.error("Void failed — please try again");
      }
    } finally {
      setVoidInFlight(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!token || !selectedOrgId || !user?.email) return;
    if (!canManageBilling) {
      toast.error(
        "You don't have permission to manage billing for this organisation. Ask your organisation owner to add a payment card.",
      );
      return;
    }
    try {
      const callbackUrl = `${window.location.origin}/portal/platform/billing`;
      const result = await billingApi.initializeCheckout(
        token,
        selectedOrgId,
        callbackUrl,
        user.email,
      );
      if (result.authorization_url) {
        window.location.href = result.authorization_url;
      }
    } catch (err) {
      if (err instanceof PortalApiError && err.isPermissionDenied) {
        toast.error(
          "You don't have permission to manage billing for this organisation. Ask your organisation owner to add a payment card.",
        );
      } else {
        toast.error("Failed to initialize payment setup");
      }
    }
  };

  // Item 8: compulsory. Mirrors the backend validators — the API 422s a blank
  // or null value either way, so this is the explanation, not the enforcement.
  // A non-blank Xero customer SUPPLIES the entity name (the save payload sets
  // billing_entity_name from it), and the entity input is disabled while it is
  // set. Without this exemption an org whose entity name is still blank — which
  // is 16 of the 18 auto_charge orgs in prod, i.e. exactly the population item 9
  // exists for — could neither type an entity name (input disabled) nor save
  // (Save disabled on the required-error). A deadlock on the one screen meant
  // to clear the backlog.
  const billingEntityNameError =
    billingEntityName.trim() === "" && xeroContactName.trim() === ""
      ? "Billing entity name is required"
      : null;
  const billingEmailError =
    billingEmail.trim() === "" ? "Billing email is required" : null;
  // Blanking a STORED Xero customer cannot be saved: the backend refuses a
  // blank or null value, so the payload omits the key and the stored name
  // survives. Silently, as it stood — the form cleared its own state and
  // toasted success, so a user detaching a wrong contact was told it worked
  // while invoices kept going to it. Say so instead of lying.
  const xeroContactClearError =
    originalXeroContactName.trim() !== "" && xeroContactName.trim() === ""
      ? "Clearing the Xero customer is not supported — type the correct customer name to change it."
      : null;
  const billingContactHasErrors =
    billingEntityNameError !== null ||
    billingEmailError !== null ||
    xeroContactClearError !== null;

  const handleSaveBillingContact = async () => {
    if (!token || !selectedOrgId) return;
    setSavingBillingContact(true);
    try {
      const trimmedEntityName = billingEntityName.trim();
      const trimmedEmail = billingEmail.trim();
      const trimmedVat = vatNumber.trim();
      const trimmedXeroContact = xeroContactName.trim();
      // `vat_number` is OPTIONAL in this payload, and deliberately so: when the
      // field is not on screen the key is omitted entirely rather than sent as
      // null, so a save never blows away a stored value the user could not see.
      // Which orgs show the field is decided by the resolved VAT TREATMENT, not
      // the currency — see the `showVatNumber` gate below.
      const payload: {
        billing_entity_name: string;
        billing_email: string;
        xero_contact_name?: string;
        vat_number?: string | null;
        address_line1: string | null;
        address_line2: string | null;
        city: string | null;
        state: string | null;
        postal_code: string | null;
        country: string | null;
      } = {
        // NO null-coercion on these two (item 8). Sending null was how a blank
        // field silently CLEARED a stored billing entity or email, and the
        // backend now 422s it — the save button is disabled while either is
        // blank, so this can only ever carry a real value.
        billing_entity_name: trimmedEntityName,
        billing_email: trimmedEmail,
        address_line1: address.address_line1.trim() || null,
        address_line2: address.address_line2.trim() || null,
        city: address.city.trim() || null,
        state: address.state.trim() || null,
        postal_code: address.postal_code.trim() || null,
        country: address.country.trim() || null,
      };
      // Mirrors the field's own visibility rule — see `showVatNumber`. Sending
      // it on a currency test while the field is shown on a treatment test
      // would silently drop an edit the admin could see themselves making.
      if (showVatNumber) {
        payload.vat_number = trimmedVat === "" ? null : trimmedVat;
      }
      // Omitted entirely when blank rather than sent as null: the backend
      // refuses a blank value, and an org with no Xero contact yet — which is
      // every un-backfilled org — must still be able to save this form.
      // Sending it also renames the billing entity to match, server-side.
      if (trimmedXeroContact !== "") {
        payload.xero_contact_name = trimmedXeroContact;
        payload.billing_entity_name = trimmedXeroContact;
      }
      await platformApi.updateOrganisationSettings(token, selectedOrgId, payload);
      const savedEntityName =
        trimmedXeroContact !== "" ? trimmedXeroContact : trimmedEntityName;
      setOriginalBillingEntityName(savedEntityName);
      setBillingEntityName(savedEntityName);
      setOriginalXeroContactName(trimmedXeroContact);
      setXeroContactName(trimmedXeroContact);
      setOriginalBillingEmail(trimmedEmail);
      setOriginalVatNumber(trimmedVat);
      setBillingEmail(trimmedEmail);
      setVatNumber(trimmedVat);
      setOriginalAddress(address);
      setEditingBillingContact(false);
      toast.success("Billing contact saved");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save: ${msg}`);
    } finally {
      setSavingBillingContact(false);
    }
  };

  const handleCancelBillingContact = () => {
    setBillingEntityName(originalBillingEntityName);
    setXeroContactName(originalXeroContactName);
    setBillingEmail(originalBillingEmail);
    setVatNumber(originalVatNumber);
    setAddress(originalAddress);
    setEditingBillingContact(false);
  };

  const handleTriggerBilling = async (charge: boolean) => {
    if (!token || !selectedOrgId) return;
    // Only the charge path is irreversible (money moves) — confirm that one.
    if (
      charge &&
      !window.confirm(
        `This will create the invoice AND charge ${currentOrgName}'s saved card` +
          " via Paystack for the previous calendar month. Continue?",
      )
    ) {
      return;
    }
    setTriggeringAction(charge ? "charge" : "no_charge");
    setTriggerResult(null);
    setTriggerError(null);
    try {
      const result = await billingApi.adminTriggerBilling(
        token,
        selectedOrgId,
        charge,
      );
      setTriggerResult(result);
      // The run is now acknowledged and executes server-side in the background.
      // Surface a "started" toast and refresh the invoice view shortly so the
      // result (the new invoice) appears without a blocking request.
      toast.success(
        `Billing run started for ${currentOrgName}` +
          (charge ? " — invoice + charge" : " — invoice only, no charge") +
          ". The invoice will appear under Invoices shortly.",
      );
      window.setTimeout(() => {
        void loadBillingData();
      }, 8000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setTriggerError(msg);
      toast.error(`Billing trigger failed: ${msg}`);
    } finally {
      setTriggeringAction(null);
    }
  };

  const handleSetBillingMode = async (
    mode: "off" | "invoice_only" | "auto_charge",
  ) => {
    if (!token || !selectedOrgId) return;
    setSavingBillingMode(true);
    try {
      await billingApi.setBillingMode(token, selectedOrgId, mode);
      setBillingMode(mode);
      toast.success("Billing mode updated");
      // Refresh the invoice/billing block so derived state stays in sync.
      void loadBillingData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Could not update billing mode: ${msg}`);
    } finally {
      setSavingBillingMode(false);
    }
  };

  /**
   * "AUTO" is the select's sentinel for "no override" and must be sent to the
   * API as an explicit JSON `null` — the control plane reads the body with
   * `exclude_unset`, so omitting the key would mean "leave the override in
   * place" and the control would silently do nothing.
   */
  const handleSetVatTreatment = async (value: VatTreatment | "AUTO") => {
    if (!token || !selectedOrgId) return;
    const next = value === "AUTO" ? null : value;
    const previous = vatTreatment;
    setSavingVatTreatment(true);
    try {
      await billingApi.updateOrganisationBilling(token, selectedOrgId, {
        vat_treatment: next,
      });
      setVatTreatment(next);
      toast.success("VAT treatment updated");
      void loadBillingData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      // Put the control back where it was: leaving it showing a value the
      // server rejected is how an admin concludes a change took effect.
      setVatTreatment(previous);
      toast.error(`Could not update VAT treatment: ${msg}`);
    } finally {
      setSavingVatTreatment(false);
    }
  };

  /** The country row backing this org, for showing what "Auto" resolves to. */
  const orgCountryRow = countryRows.find((c) => c.code === orgCountryCode);

  /** Treatment in force for this org — override first, else its country's. */
  const billToVat = resolveVatTreatment(
    countryRows,
    orgCountryCode,
    vatTreatment,
  );

  /**
   * Whether to offer a VAT-number field: when the org is actually charged VAT,
   * or when it already has a number stored. The second clause matters because
   * hiding a stored value leaves an admin unable to correct it — and it reads
   * `originalVatNumber` rather than the live field so clearing the input does
   * not make the input vanish mid-edit.
   */
  // The second clause is safe against cross-org staleness because the load's
  // catch now clears `originalVatNumber` along with everything else it loads —
  // so a failed load after an org switch cannot leave another org's number
  // holding this field open.
  const showVatNumber =
    billToVat.treatment === "STANDARD" || originalVatNumber.trim() !== "";

  /**
   * Bill-To options in LABEL space, guarded the same way the org picker is: a
   * stored free-text country that matches no label would otherwise render the
   * select blank and invite an admin to overwrite a real address.
   */
  const billToCountryOptions = useMemo(() => {
    const current = address.country;
    if (!current || countryOptions.some((c) => c.label === current)) {
      return countryOptions;
    }
    return [{ value: current, label: current }, ...countryOptions];
  }, [countryOptions, address.country]);

  if (orgLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Select an organisation from the header to view billing data</p>
        </div>
      </div>
    );
  }

  if (!canViewBilling) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>You don't have permission to view billing information</p>
          <p className="text-xs mt-2">Contact your organisation administrator for access</p>
        </div>
      </div>
    );
  }

  // Calculate totals for SUBSCRIBED tier-based services only
  const subscribedServices = new Set<string>(
    perServiceLimits?.services
      ?.filter((s) => s.tier !== "none")
      ?.map((s) => s.service_name) || [],
  );

  const subscribedTotals = billingUsage?.services
    ? Object.entries(billingUsage.services)
        .filter(([serviceName]) => subscribedServices.has(serviceName))
        .reduce(
          (acc, [, usage]) => ({
            total_cost_usd:
              acc.total_cost_usd +
              parseFloat(String(usage?.total_cost_usd || 0)),
            total_tokens: acc.total_tokens + (usage?.total_tokens || 0),
          }),
          { total_cost_usd: 0, total_tokens: 0 },
        )
    : { total_cost_usd: 0, total_tokens: 0 };

  // Count active subscriptions
  const activeSubscriptionCount = perServiceLimits?.services?.filter(
    (s) => s.tier !== "none",
  ).length || 0;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Usage & Billing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor service usage and billing for {currentOrgName}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadBillingData}
          disabled={loading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className={`grid gap-4 ${isAdmin ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
        {/* Period Selector Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Billing Period</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={goToPreviousMonth}
                aria-label="Previous month"
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <div className="text-lg font-bold">
                  {formatMonth(selectedMonth)}
                </div>
                {/*
                  `timeZone: "UTC"` IS LOAD-BEARING, NOT TIDINESS.

                  `getMonthBounds` returns UTC instants. Formatted in the
                  viewer's local zone, July's end (Jul 31 23:59:59Z) renders as
                  "Aug 1" for a SAST reader and its start (Jul 1 00:00:00Z) as
                  "Jun 30" in New York — so this label read "Jul 1 - Aug 1",
                  which is the precise string billing-invoice-period.spec.ts
                  bans two panels away in Invoice History. Before the bounds
                  moved to UTC both halves were local and agreed by accident;
                  making one UTC without the other is what broke it.

                  It is also a latent CI time bomb: that spec's negative
                  assertions scan the whole page under a real clock, so in an
                  actual July this label would match them, fail e2e_tests, and
                  block every production deploy for the month.
                */}
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="month-picker-range"
                >
                  {monthStart.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })}{" "}
                  -{" "}
                  {monthEnd.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC",
                  })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={goToNextMonth}
                disabled={isCurrentMonth()}
                aria-label="Next month"
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Bill Card - Customer-facing */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Monthly Bill
              {/*
                The month is CLOSED and these figures are the ones on the
                invoice the customer was sent — not a recalculation at today's
                prices. Badging it is the customer-visible half of cp #549:
                without it the page shows a stored total in a panel whose whole
                idiom is "your current bill", and there is nothing to
                distinguish "what you were charged" from "what you would be
                charged if we ran it now".
              */}
              {storedInvoice && (
                <Badge variant="secondary" data-testid="as-invoiced-badge">
                  As invoiced
                </Badge>
              )}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="monthly-bill-total">
              {formatCurrency(customerInvoice?.total || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {customerInvoice && customerInvoice.vat_amount > 0 ? (
                <>
                  {formatCurrency(customerInvoice.subtotal)} + {formatCurrency(customerInvoice.vat_amount)} VAT
                </>
              ) : customerInvoice && customerInvoice.subtotal_overages > 0 ? (
                <MeteredSummary
                  subscription={customerInvoice.subtotal_subscription}
                  split={meteredSplit(customerInvoice)}
                  formatCurrency={formatCurrency}
                />
              ) : (
                "Subscription fees"
              )}
            </p>
            {customerInvoice && (
              <p className="text-xs text-muted-foreground mt-1">
                {/* A zero rate is not one state, and "Ex VAT" is wrong for the
                    commonest of them: a zero-rated export is a taxable supply
                    at 0%. The treatment comes from the server; only an invoice
                    raised before the column existed has none. */}
                {customerInvoice.vat_rate > 0
                  ? `Incl. ${(customerInvoice.vat_rate * 100).toFixed(0)}% VAT`
                  : customerInvoice.vat_treatment
                    ? vatTreatmentLabel(customerInvoice.vat_treatment)
                    : // No treatment recorded (an invoice raised before the
                      // column existed). State the RATE, which is known, rather
                      // than name a treatment, which is not — inferring one
                      // from a bare zero is the defect this line replaced.
                      "0% VAT"}
              </p>
            )}
            {storedInvoice && (
              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="as-invoiced-note"
              >
                Final invoice for this period — not a live recalculation.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Active Subscriptions Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Services
            </CardTitle>
            <Gauge className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSubscriptionCount}</div>
            <p className="text-xs text-muted-foreground">
              {activeSubscriptionCount === 1
                ? "subscribed service"
                : "subscribed services"}
            </p>
          </CardContent>
        </Card>

        {/* Admin-only: Margin Card (shows full usage margin - target margin) */}
        {isAdmin && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Target Margin
              </CardTitle>
              <DollarSign className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-700">
                {marginAnalysis?.full_usage
                  ? `${marginAnalysis.full_usage.margin_percentage.toFixed(0)}%`
                  : "-"}
              </div>
              <p className="text-xs text-blue-600">
                {marginAnalysis?.full_usage
                  ? `At full usage: ${formatCurrency(marginAnalysis.full_usage.margin)}`
                  : "Loading..."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="limits" className="flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Limits
            {usageLimits?.any_approaching && !usageLimits?.any_blocked && (
              <span className="ml-1 h-2 w-2 rounded-full bg-yellow-500" />
            )}
            {usageLimits?.any_blocked && (
              <span className="ml-1 h-2 w-2 rounded-full bg-red-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          {isAdmin && (
            <TabsTrigger
              value="admin"
              className="flex items-center gap-2 text-blue-600"
            >
              <Shield className="h-4 w-4" />
              Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* Limits Tab - Shows per-service limits (for all users) */}
        <TabsContent value="limits" className="mt-6">
          <PerServiceUsageLimitsCard
            data={perServiceLimits}
            isLoading={loading}
          />
        </TabsContent>

        {/* Usage Tab - Simplified for customers (no costs) */}
        <TabsContent value="usage" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Service Usage</CardTitle>
              <CardDescription className="text-xs">
                Track your usage across subscribed services
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : billingUsage?.services ? (
                <div className="space-y-4">
                  {(() => {
                    const filteredServices = Object.entries(
                      billingUsage.services,
                    ).filter(([serviceName]) =>
                      subscribedServices.has(serviceName),
                    );

                    if (filteredServices.length === 0) {
                      return (
                        <div className="text-center py-8 text-muted-foreground">
                          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No subscribed services</p>
                          <p className="text-xs mt-2">
                            Subscribe to services to track usage
                          </p>
                        </div>
                      );
                    }

                    return filteredServices.map(([serviceName, usage]) => (
                      <ServiceUsageCard
                        key={serviceName}
                        serviceName={serviceName}
                        usage={usage}
                        showCosts={false}
                      />
                    ));
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No usage data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6">
          <div className="space-y-6">
            <CustomerInvoiceBreakdown
              variant="customer"
              customerInvoice={customerInvoice}
              storedInvoice={storedInvoice}
              periodLabel={formatMonth(selectedMonth)}
              isCurrentMonth={isCurrentMonth()}
              loading={loading}
              billingCurrency={billingCurrency}
              formatCurrency={formatCurrency}
            />

            {/* Billing Contact Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Billing Contact</CardTitle>
                    <CardDescription className="text-xs">
                      Legal billing entity, email for receipts &amp; dunning
                      notifications, and VAT number for invoices
                    </CardDescription>
                  </div>
                  {!editingBillingContact ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingBillingContact(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelBillingContact}
                        disabled={savingBillingContact}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveBillingContact}
                        disabled={savingBillingContact || billingContactHasErrors}
                      >
                        {savingBillingContact && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>
                    Xero Customer
                    <span className="ml-1 text-xs text-muted-foreground">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    value={xeroContactName}
                    onChange={(e) => setXeroContactName(e.target.value)}
                    data-testid="xero-contact-name"
                    placeholder="e.g. Your Company (Pty) Ltd"
                    disabled={!editingBillingContact}
                    className={!editingBillingContact ? "bg-muted" : ""}
                    aria-invalid={xeroContactClearError !== null}
                  />
                  {editingBillingContact && xeroContactClearError ? (
                    <p
                      className="text-xs text-destructive"
                      data-testid="xero-contact-clear-error"
                    >
                      {xeroContactClearError}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    The contact in Xero this organisation&apos;s invoices belong
                    to. Matched by name, so it must be typed exactly as it
                    appears in Xero. Setting it renames the billing entity to
                    match.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>
                    Billing Entity Name
                    <span className="ml-1 text-destructive">*</span>
                  </Label>
                  <Input
                    value={billingEntityName}
                    onChange={(e) => setBillingEntityName(e.target.value)}
                    data-testid="billing-entity-name"
                    placeholder="e.g. Your Company (Pty) Ltd"
                    disabled={!editingBillingContact || xeroContactName.trim() !== ""}
                    className={
                      !editingBillingContact || xeroContactName.trim() !== ""
                        ? "bg-muted"
                        : ""
                    }
                    aria-invalid={billingEntityNameError !== null}
                  />
                  {editingBillingContact && billingEntityNameError ? (
                    <p className="text-xs text-destructive">
                      {billingEntityNameError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {xeroContactName.trim() !== ""
                        ? "Set from the Xero customer above — they must match, or the invoice goes to the wrong Xero contact."
                        : "Legal entity invoices are billed to."}
                    </p>
                  )}
                </div>
                {/* Two columns exactly when there IS a second child. This keyed
                    on currency — the last survivor of the gate removed below,
                    and wrong in both directions once that gate moved: a USD
                    STANDARD-override org got its VAT field full-width, a ZAR
                    zero-rated org got a two-column grid holding one child. */}
                <div
                  className={`grid gap-4 ${showVatNumber ? "md:grid-cols-2" : ""}`}
                >
                  <div className="space-y-2">
                    <Label>
                      Billing Email
                      <span className="ml-1 text-destructive">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                      placeholder="accounts@example.com"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                      aria-invalid={billingEmailError !== null}
                    />
                    {editingBillingContact && billingEmailError ? (
                      <p className="text-xs text-destructive">
                        {billingEmailError}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Where receipts and dunning notices are sent
                      </p>
                    )}
                  </div>
                  {/* Gated on the RESOLVED TREATMENT, not the currency. The old
                      `billingCurrency === "ZAR"` gate was wrong in both
                      directions: a ZAR-billed Namibian org is zero-rated and
                      still got a VAT field, while a USD org overridden to
                      STANDARD — a state this very page can now set — is charged
                      15% and had nowhere to record its VAT number. */}
                  {showVatNumber && (
                    <div className="space-y-2">
                      <Label>VAT Number</Label>
                      <Input
                        value={vatNumber}
                        onChange={(e) => setVatNumber(e.target.value)}
                        placeholder="e.g. 4123456789"
                        disabled={!editingBillingContact}
                        className={!editingBillingContact ? "bg-muted" : ""}
                      />
                      <p className="text-xs text-muted-foreground">
                        {/* Was "alongside 15% VAT" — hardcoded, and wrong for a
                            ZAR-billed foreign org: Beach Hotel Group is
                            Namibian, invoiced in ZAR, and zero-rated. Currency
                            does not decide VAT. */}
                        Optional — appears on invoices.{" "}
                        {vatPricingHint(billToVat.treatment, billToVat.rate)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Billing address — the "Bill To" block that appears on the
                    invoice and syncs to the Xero contact. */}
                <div className="mt-6 space-y-2">
                  <Label>Billing Address</Label>
                  <p className="text-xs text-muted-foreground">
                    Appears on invoices and on your accounting-system contact
                  </p>
                  <div className="grid gap-3 md:grid-cols-2 pt-1">
                    <Input
                      value={address.address_line1}
                      onChange={(e) =>
                        setAddress({ ...address, address_line1: e.target.value })
                      }
                      placeholder="Address line 1"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                    />
                    <Input
                      value={address.address_line2}
                      onChange={(e) =>
                        setAddress({ ...address, address_line2: e.target.value })
                      }
                      placeholder="Address line 2 (optional)"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                    />
                    <Input
                      value={address.city}
                      onChange={(e) =>
                        setAddress({ ...address, city: e.target.value })
                      }
                      placeholder="City"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                    />
                    <Input
                      value={address.state}
                      onChange={(e) =>
                        setAddress({ ...address, state: e.target.value })
                      }
                      placeholder="Province / State"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                    />
                    <Input
                      value={address.postal_code}
                      onChange={(e) =>
                        setAddress({ ...address, postal_code: e.target.value })
                      }
                      placeholder="Postal code"
                      disabled={!editingBillingContact}
                      className={!editingBillingContact ? "bg-muted" : ""}
                    />
                    <Select
                      value={address.country}
                      onValueChange={(v) =>
                        setAddress({ ...address, country: v })
                      }
                      disabled={!editingBillingContact}
                    >
                      <SelectTrigger
                        className={!editingBillingContact ? "bg-muted" : ""}
                      >
                        <SelectValue placeholder="Country" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* NOTE the value is the LABEL, not the code. This
                            select feeds the free-text `Organisation.Country`
                            postal address on the invoice; the org's ISO
                            `CountryCode` (which decides VAT) is a different
                            column edited on the Organisations page. Do not
                            unify the two — they have different value spaces. */}
                        {billToCountryOptions.map((c) => (
                          <SelectItem key={c.value} value={c.label}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Payment Method</CardTitle>
                    <CardDescription className="text-xs">
                      Manage your saved payment methods
                    </CardDescription>
                  </div>
                  {canManageBilling && (
                    <Button size="sm" onClick={handleAddPaymentMethod}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Add Card
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {paymentMethods.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No payment methods on file</p>
                    <p className="text-xs mt-1">
                      {canManageBilling
                        ? "Add a card to enable automatic billing"
                        : "Ask your organisation owner to add a payment card"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`flex items-center justify-between rounded-lg border p-3 ${
                          method.is_default ? "border-primary bg-primary/5" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium capitalize">
                                {method.card_type || method.type}
                              </span>
                              <span className="text-sm text-muted-foreground font-mono">
                                &#8226;&#8226;&#8226;&#8226; {method.last4 || "****"}
                              </span>
                              {method.is_default && (
                                <Badge variant="secondary" className="text-xs">
                                  Default
                                </Badge>
                              )}
                            </div>
                            {method.exp_month && method.exp_year && (
                              <p className="text-xs text-muted-foreground">
                                Expires {String(method.exp_month).padStart(2, "0")}/{method.exp_year}
                              </p>
                            )}
                          </div>
                        </div>
                        {canManageBilling && (
                          <div className="flex items-center gap-1">
                            {!method.is_default && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSetDefaultMethod(method.id)}
                                title="Set as default"
                              >
                                <Star className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMethod(method.id)}
                              title="Remove payment method"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                  <Shield className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Cards are added securely on{" "}
                    <span className="font-medium">Paystack</span> (
                    <a
                      href="https://paystack.com/stripe"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      a Stripe company
                    </a>
                    ) — you'll be redirected to Paystack to enter your details.{" "}
                    <span className="font-medium">
                      Larasoft never sees or stores your card number.
                    </span>{" "}
                    <a
                      href="https://paystack.com/compliance"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Invoice History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoice History</CardTitle>
                <CardDescription className="text-xs">
                  Click an invoice to view line-item breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : billingHistory && billingHistory.invoices.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8" />
                        <TableHead>Period</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billingHistory.invoices.map((invoice) => {
                        // Period boundaries are calendar days, formatted in UTC
                        // — period_end is 23:59:59Z, which local-time
                        // formatting rolled into the next month for SAST
                        // viewers ("Jul 1 - Aug 1" for a July invoice).
                        const periodLabel = formatCalendarMonth(invoice.period_start);
                        const isExpanded = expandedInvoiceId === invoice.id;
                        const isPaid = invoice.status === "PAID";
                        const isOverdue = invoice.status === "OVERDUE";
                        // Pay Now is offered for ISSUED as well as OVERDUE,
                        // matching the statuses the backend's claim predicate
                        // accepts (`Status IN ('ISSUED','OVERDUE')`). Paying
                        // before the due date is legitimate, and the button was
                        // withheld from exactly the customers who wanted to.
                        // Kept SEPARATE from `isOverdue`, which still drives the
                        // red "Payment failed" line and the status badge — an
                        // ISSUED invoice has not failed anything.
                        const isPayable =
                          invoice.status === "OVERDUE" ||
                          invoice.status === "ISSUED";
                        // The correction door (cp #565 item 3) — see
                        // `invoiceVoidAvailability` for the predicate and why
                        // a claimed row is disabled rather than hidden.
                        const voidAvailability =
                          invoiceVoidAvailability(invoice);
                        const statusVariant = isPaid
                          ? "success"
                          : isOverdue
                            ? "destructive"
                            : "secondary";
                        const statusLabel = isPaid
                          ? "Paid"
                          : isOverdue
                            ? "Overdue"
                            : invoice.status === "ISSUED"
                              ? "Issued"
                              : invoice.status === "VOID"
                                ? "Void"
                                : "Draft";

                        return (
                          <React.Fragment key={invoice.id}>
                            <TableRow
                              className={`cursor-pointer ${isExpanded ? "bg-muted/30" : ""}`}
                              onClick={() =>
                                setExpandedInvoiceId(isExpanded ? null : invoice.id)
                              }
                            >
                              <TableCell className="w-8 pr-0">
                                <ChevronDown
                                  className={`h-4 w-4 text-muted-foreground transition-transform ${
                                    isExpanded ? "rotate-0" : "-rotate-90"
                                  }`}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{periodLabel}</div>
                                {/*
                                  Testid so the spec can assert THIS period and
                                  not the month picker's, which renders the same
                                  "Jul 1 - Jul 31" text above the tabs whenever
                                  July is the selected month. Unscoped, the two
                                  collide under a real July clock and Playwright
                                  fails strict mode with "resolved to 2
                                  elements" — taking e2e_tests, and therefore
                                  every production deploy, down for the month.
                                */}
                                <div
                                  className="text-xs text-muted-foreground"
                                  data-testid="invoice-period-range"
                                >
                                  {formatCalendarDay(invoice.period_start)} -{" "}
                                  {formatCalendarDay(invoice.period_end)}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="font-mono">
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: invoice.currency,
                                  }).format(invoice.total / 100)}
                                </div>
                                {invoice.fx_note && (
                                  <div
                                    className="text-xs text-muted-foreground max-w-[18rem]"
                                    data-testid="invoice-fx-note"
                                  >
                                    {invoice.fx_note}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={statusVariant}>
                                  {statusLabel}
                                </Badge>
                                {isOverdue && (
                                  <p className="text-xs text-destructive mt-1">
                                    Payment failed — please retry or update your payment method
                                  </p>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {invoice.payment_reference || "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  {isPayable && canManageBilling && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={payingInvoiceId !== null}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePayInvoice(invoice.id);
                                      }}
                                    >
                                      {payingInvoiceId === invoice.id
                                        ? "Paying…"
                                        : "Pay Now"}
                                    </Button>
                                  )}
                                  {isAdmin && voidAvailability.offered && (
                                    <div className="flex items-center gap-2">
                                      {/*
                                        THE REASON A CLAIMED ROW IS SHUT IS
                                        RENDERED, NOT HOVERED.

                                        It was a `title` on the button, then a
                                        `title` on a wrapper span. Both are
                                        wrong for this: `buttonVariants` sets
                                        `disabled:pointer-events-none`
                                        (`ui/button.tsx:8`) so the first never
                                        rendered at all, and the second is
                                        MOUSE-ONLY — a disabled button is not
                                        focusable, so no keyboard or
                                        screen-reader user could ever reach it.

                                        The remedy (release the payment claim
                                        first) is the whole reason this is
                                        disabled rather than hidden, and
                                        voiding a `UPG-` claim destroys the
                                        only copy of the upgrade's ledger
                                        figures. That is not something to make
                                        anyone hover to discover.
                                      */}
                                      {voidAvailability.blockedByClaim && (
                                        <span
                                          data-testid="void-invoice-hint"
                                          className="text-xs text-muted-foreground max-w-[16rem] text-right"
                                        >
                                          {voidAvailability.hint}
                                        </span>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-destructive hover:text-destructive"
                                        data-testid="void-invoice"
                                        disabled={voidAvailability.blockedByClaim}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setVoidReason("");
                                          setVoidTarget(invoice);
                                        }}
                                      >
                                        Void
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow key={`${invoice.id}-detail`}>
                                <TableCell colSpan={6} className="bg-muted/20 p-0">
                                  {/*
                                    The INVOICE's currency, not the org's
                                    billing currency (`formatCurrency`). They
                                    differ for a USD-priced org, whose invoices
                                    are issued in ZAR (cp #702): formatting
                                    those cents with the page's USD formatter
                                    would print "$736.61" for R736.61.
                                  */}
                                  <StoredInvoiceLineItems
                                    invoice={invoice}
                                    formatCurrency={(amount) =>
                                      new Intl.NumberFormat(
                                        invoice.currency === "ZAR" ? "en-ZA" : "en-US",
                                        {
                                          style: "currency",
                                          currency: invoice.currency,
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      ).format(amount)
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices available yet</p>
                    <p className="text-xs mt-2">
                      Invoices will appear here after your first billing cycle
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Admin Tab - Detailed cost breakdown and margin analysis (admin only) */}
        {isAdmin && (
          <TabsContent value="admin" className="mt-6">
            <div className="space-y-6">
              {/* Admin Notice */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <Shield className="h-4 w-4" />
                <span>
                  This tab shows internal cost data and margin analysis. Only visible to
                  administrators.
                </span>
              </div>

              {/* Margin Analysis Cards */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="border-green-200 bg-green-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      Customer Bill
                      {/*
                        Load-bearing for an admin reading this tab: every other
                        figure beside it (internal cost, both margins) is
                        computed at today's prices, while this one is the stored
                        invoice. Unlabelled, a margin that moved because a tier
                        price changed after billing reads as a margin problem.
                      */}
                      {storedInvoice && (
                        <Badge variant="secondary">As invoiced</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-700">
                      {formatCurrency(customerInvoice?.total || 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {customerInvoice?.vat_rate && customerInvoice.vat_rate > 0
                        ? `Incl. ${(customerInvoice.vat_rate * 100).toFixed(0)}% VAT (${billingCurrency})`
                        : `Subscription + usage (${billingCurrency})`}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Current Internal Cost</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-700">
                      {marginAnalysis?.current
                        ? formatCurrency(marginAnalysis.current.internal_cost)
                        : formatCurrency(subscribedTotals.total_cost_usd)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Actual usage this period
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-purple-200 bg-purple-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Current Margin</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-700">
                      {marginAnalysis?.current
                        ? `${marginAnalysis.current.margin_percentage.toFixed(0)}%`
                        : "-"}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {marginAnalysis?.current
                        ? `${formatCurrency(marginAnalysis.current.margin)} profit`
                        : "Based on actual usage"}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-orange-200 bg-orange-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Full Usage Margin</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-700">
                      {marginAnalysis?.full_usage
                        ? `${marginAnalysis.full_usage.margin_percentage.toFixed(0)}%`
                        : "-"}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {marginAnalysis?.full_usage
                        ? `${formatCurrency(marginAnalysis.full_usage.margin)} at 100% usage`
                        : "If using full allowances"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Billing Mode (admin action) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Billing Mode</CardTitle>
                  <CardDescription className="text-xs">
                    Controls what the monthly scheduler does for{" "}
                    {currentOrgName}. The buttons below always let you bill on
                    demand regardless of this setting.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <TooltipProvider delayDuration={150}>
                    <Select
                      value={billingMode}
                      onValueChange={(v) =>
                        handleSetBillingMode(
                          v as "off" | "invoice_only" | "auto_charge",
                        )
                      }
                      disabled={savingBillingMode}
                    >
                      <SelectTrigger className="w-full max-w-sm">
                        <SelectValue placeholder="Select billing mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">
                          Off — not in the monthly run
                        </SelectItem>
                        <SelectItem value="invoice_only">
                          Invoice only — auto-invoice, never charge
                        </SelectItem>
                        <SelectItem value="auto_charge">
                          Auto-charge — auto-invoice and charge card
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Per-mode explainer so there's no ambiguity */}
                    <div className="text-xs text-muted-foreground space-y-1 max-w-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="cursor-help underline decoration-dotted">
                            What do these mean?
                          </p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs space-y-1">
                          <p>
                            <b>Off:</b> the scheduler ignores this org. Invoices
                            are only ever created by a button below. No automatic
                            invoicing, no automatic charging.
                          </p>
                          <p>
                            <b>Invoice only:</b> the scheduler auto-creates a
                            monthly Xero invoice but never charges a card. You
                            collect manually (the customer pays, or you press
                            "Create invoice + charge now").
                          </p>
                          <p>
                            <b>Auto-charge:</b> the scheduler auto-creates the
                            monthly invoice and charges the saved Paystack card.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      {billingMode === "off" && (
                        <p>
                          Currently <b>Off</b> — nothing automatic. Bill this org
                          using the buttons below.
                        </p>
                      )}
                      {billingMode === "invoice_only" && (
                        <p>
                          Currently <b>Invoice only</b> — a monthly invoice is
                          raised automatically, but it is never charged.
                        </p>
                      )}
                      {billingMode === "auto_charge" && (
                        <p>
                          Currently <b>Auto-charge</b> — the monthly invoice is
                          raised and the saved card is charged automatically.
                        </p>
                      )}
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>

              {/* VAT Treatment (admin action) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">VAT Treatment</CardTitle>
                  <CardDescription className="text-xs">
                    How {currentOrgName}'s supplies are treated for VAT. This is
                    a treatment, not a rate — a zero-rated export is
                    VAT201-reportable at 0% and an exempt supply is not, and a
                    bare 0 cannot tell them apart. Defaults to the country's
                    treatment; override only when this customer differs.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Select
                    value={vatTreatment ?? "AUTO"}
                    onValueChange={(v) =>
                      handleSetVatTreatment(v as VatTreatment | "AUTO")
                    }
                    disabled={savingVatTreatment}
                  >
                    <SelectTrigger
                      className="w-full max-w-sm"
                      data-testid="vat-treatment-select"
                    >
                      <SelectValue placeholder="Select VAT treatment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AUTO">
                        Auto — use the country default
                      </SelectItem>
                      <SelectItem value="STANDARD">
                        Standard rated
                      </SelectItem>
                      <SelectItem value="ZERO_RATED_EXPORT">
                        Zero-rated export
                      </SelectItem>
                      <SelectItem value="EXEMPT">Exempt</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-muted-foreground space-y-1 max-w-sm">
                    {vatTreatment === null ? (
                      <p>
                        Currently <b>Auto</b>
                        {orgCountryRow ? (
                          <>
                            {" "}
                            — {orgCountryRow.name} is{" "}
                            <b>{treatmentLabel(orgCountryRow.vat_treatment)}</b>.
                          </>
                        ) : (
                          <> — resolved from the organisation's country.</>
                        )}
                      </p>
                    ) : (
                      <p>
                        Overridden to <b>{treatmentLabel(vatTreatment)}</b>
                        {orgCountryRow ? (
                          <>
                            , ignoring {orgCountryRow.name}'s default of{" "}
                            {treatmentLabel(orgCountryRow.vat_treatment)}.
                          </>
                        ) : (
                          "."
                        )}
                      </p>
                    )}
                    {vatTreatment === "STANDARD" && (
                      <p>
                        Standard rating charges the supplier's VAT rate
                        (South Africa's), whatever this organisation's country
                        is.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Run Billing Now (admin action) — two explicit outcomes */}
              <Card className="border-amber-200 bg-amber-50/30">
                <CardHeader>
                  <CardTitle className="text-base">Run Billing Now</CardTitle>
                  <CardDescription className="text-xs">
                    Bill {currentOrgName} for the previous calendar month on
                    demand. Choose whether to take payment — this overrides the
                    billing mode above for this one run.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <TooltipProvider delayDuration={150}>
                    <div className="flex flex-wrap gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={() => handleTriggerBilling(false)}
                            disabled={triggeringAction !== null}
                            variant="outline"
                          >
                            {triggeringAction === "no_charge" ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="mr-2 h-4 w-4" />
                            )}
                            Create invoice — no charge
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          Creates the invoice. No card is charged. Safe to use
                          for proving the bill is correct.
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={() => handleTriggerBilling(true)}
                            disabled={triggeringAction !== null}
                            variant="default"
                          >
                            {triggeringAction === "charge" ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Zap className="mr-2 h-4 w-4" />
                            )}
                            Create invoice + charge now
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          Creates the invoice AND charges the org's saved Paystack
                          card. Real money moves — you'll be asked to confirm.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>

                  {triggerResult && (
                    <div className="text-sm space-y-1 p-3 rounded-md bg-white border">
                      <div>
                        <span className="text-muted-foreground">Status: </span>
                        <span className="text-green-700 font-medium">
                          STARTED
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Run: </span>
                        <span className="font-medium">
                          {triggerResult.charge
                            ? "invoice + charge"
                            : "invoice only, no charge"}
                        </span>
                      </div>
                      <div className="text-blue-700">
                        Running in the background — the invoice will appear under
                        Invoices shortly (refreshing automatically).
                      </div>
                    </div>
                  )}

                  {triggerError && (
                    <div className="text-sm text-red-700 p-3 rounded-md bg-red-50 border border-red-200">
                      {triggerError}
                    </div>
                  )}
                </CardContent>
              </Card>

              <CustomerInvoiceBreakdown
                variant="admin"
                customerInvoice={customerInvoice}
                storedInvoice={storedInvoice}
                periodLabel={formatMonth(selectedMonth)}
                isCurrentMonth={isCurrentMonth()}
                loading={loading}
                billingCurrency={billingCurrency}
                formatCurrency={formatCurrency}
              />

              {/* Internal Cost Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Internal Cost Breakdown</CardTitle>
                  <CardDescription className="text-xs">
                    Detailed breakdown of usage costs by service (what we pay)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : billingUsage?.services ? (
                    <AdminCostBreakdownTable
                      services={billingUsage.services}
                      subscribedServices={subscribedServices}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No cost data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/*
        The void confirmation, mounted at the page root rather than inside the
        Invoice History card so switching tabs cannot unmount a dialog with a
        typed reason in it.
      */}
      <Dialog
        open={voidTarget !== null}
        onOpenChange={(open) => {
          if (!open && !voidInFlight) {
            setVoidTarget(null);
            setVoidReason("");
          }
        }}
      >
        <DialogContent data-testid="void-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Void this invoice?</DialogTitle>
            <DialogDescription>
              {voidTarget && (
                <>
                  {formatCalendarMonth(voidTarget.period_start)} ·{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: voidTarget.currency,
                  }).format(voidTarget.total / 100)}{" "}
                  · {voidTarget.status}
                  {voidTarget.fx_note && (
                    <span
                      className="block text-xs"
                      data-testid="void-invoice-fx-note"
                    >
                      {voidTarget.fx_note}
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {/*
            Says what voiding DOES, because the two halves are easy to get
            backwards: the row is kept (this is a record, not a delete) and the
            period becomes billable again (so the next run will re-invoice it).
            Nothing here touches Xero — an already-issued Xero document is the
            operator's to credit, and saying so prevents a void being mistaken
            for a refund.
          */}
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The invoice stays in history marked Void, with who voided it and
              why. The period becomes billable again, so the next billing run
              creates a fresh invoice for it. This does not touch Xero — if a
              Xero invoice was already issued, raise a credit note there.
            </p>
            <div className="space-y-1">
              <Label htmlFor="void-reason">Reason (required)</Label>
              <Textarea
                id="void-reason"
                data-testid="void-reason"
                rows={3}
                maxLength={500}
                value={voidReason}
                placeholder="Why is this invoice being voided?"
                onChange={(e) => setVoidReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {voidReason.trim().length}/500 — at least 5 characters. This is
                recorded against the invoice and in the audit log.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={voidInFlight}
              onClick={() => {
                setVoidTarget(null);
                setVoidReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="void-invoice-confirm"
              disabled={voidInFlight || voidReasonError(voidReason) !== null}
              onClick={handleVoidInvoice}
            >
              {voidInFlight ? "Voiding…" : "Void invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Shape returned per service from the usage API. */
interface ServiceUsageData {
  total_tokens?: number;
  total_operations?: number;
  operation_count?: number;
  email_count?: number;
  object_count?: number;
  total_cost_usd?: number | string;
  storage_gib?: number | string;
  error?: string;
}

// Admin Cost Breakdown Table Component
interface AdminCostBreakdownTableProps {
  services: Record<string, ServiceUsageData>;
  subscribedServices: Set<string>;
}

function AdminCostBreakdownTable({
  services,
  subscribedServices,
}: AdminCostBreakdownTableProps) {
  const filteredServices = Object.entries(services).filter(([serviceName]) =>
    subscribedServices.has(serviceName),
  );

  if (filteredServices.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No subscribed services</p>
      </div>
    );
  }

  const subscribedTotalCost = filteredServices.reduce(
    (sum, [, usage]) => sum + parseFloat(String(usage?.total_cost_usd || 0)),
    0,
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Service</TableHead>
          <TableHead>Tokens</TableHead>
          <TableHead>Operations</TableHead>
          <TableHead>Storage</TableHead>
          <TableHead className="text-right">Cost (USD)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredServices.map(([serviceName, usage]) => {
          const cost = parseFloat(String(usage?.total_cost_usd || 0));
          const tokens = usage?.total_tokens || 0;
          const operations =
            usage?.total_operations ||
            usage?.operation_count ||
            usage?.email_count ||
            usage?.object_count ||
            0;
          const storage = parseFloat(String(usage?.storage_gib || 0));
          const hasError = usage?.error;

          return (
            <TableRow key={serviceName}>
              <TableCell className="font-medium">
                {SERVICE_NAMES[serviceName] || serviceName}
              </TableCell>
              <TableCell>
                {hasError ? (
                  <span className="text-muted-foreground text-sm">Error</span>
                ) : tokens > 0 ? (
                  <span>{tokens.toLocaleString()}</span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {hasError ? (
                  <span className="text-muted-foreground text-sm">-</span>
                ) : operations > 0 ? (
                  <span>{operations.toLocaleString()}</span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                {hasError ? (
                  <span className="text-muted-foreground text-sm">-</span>
                ) : storage > 0 ? (
                  <span>{storage.toFixed(2)} GiB</span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-right font-mono">
                {hasError ? (
                  <span className="text-red-500 text-sm">Error</span>
                ) : (
                  <span>${cost.toFixed(2)}</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        <TableRow className="bg-muted/50">
          <TableCell colSpan={4} className="font-semibold">
            Total Usage Cost
          </TableCell>
          <TableCell className="text-right font-bold font-mono">
            ${subscribedTotalCost.toFixed(2)} USD
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

// Per-Service Usage Limits Card Component
interface PerServiceUsageLimitsCardProps {
  data: PerServiceUsageLimitsResponse | null;
  isLoading: boolean;
}

function PerServiceUsageLimitsCard({
  data,
  isLoading,
}: PerServiceUsageLimitsCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeServices = data?.services?.filter(
    (s) => s.tier !== "none",
  ) || [];

  if (activeServices.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Gauge className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No active service subscriptions</p>
            <p className="text-xs mt-2">
              Subscribe to a service to see usage limits
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {activeServices.map((service) => (
        <ServiceLimitsCard key={service.service_name} service={service} />
      ))}
    </div>
  );
}

// Individual Service Limits Card
interface ServiceLimitsCardProps {
  service: ServiceUsageLimits;
}

// Exported for tests: this card is where a null usage value would crash the
// whole billing page, so it is worth rendering directly rather than mounting
// the entire route.
export function ServiceLimitsCard({ service }: ServiceLimitsCardProps) {
  const metricEntries = Object.entries(service.metrics);
  const isHardEnforcement = service.enforcement_mode === "hard";
  const needsUpgrade = isHardEnforcement && (service.any_blocked || service.any_approaching);

  if (metricEntries.length === 0) {
    return null;
  }

  return (
    <Card className={needsUpgrade && service.any_blocked ? "border-red-200" : ""}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">
              {SERVICE_NAMES[service.service_name] || service.service_name}
            </CardTitle>
            <CardDescription className="text-xs">
              Usage limits for your {service.tier} tier subscription
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {service.any_blocked && isHardEnforcement && (
              <Badge variant="destructive" className="text-xs">
                Limits Exceeded
              </Badge>
            )}
            {service.any_blocked && !isHardEnforcement && (
              <Badge
                variant="secondary"
                className="text-xs bg-amber-100 text-amber-700"
              >
                Overages Active
              </Badge>
            )}
            {service.any_approaching && !service.any_blocked && (
              <Badge
                variant="secondary"
                className="text-xs bg-yellow-100 text-yellow-700"
              >
                Approaching Limits
              </Badge>
            )}
            <Badge variant="secondary" className="capitalize">
              {service.tier}
            </Badge>
          </div>
        </div>

        {/* Upgrade CTA for free/trial tier when limits exceeded or approaching */}
        {needsUpgrade && (
          <div
            className={`mt-4 p-3 rounded-lg flex items-center justify-between ${
              service.any_blocked
                ? "bg-red-50 border border-red-200"
                : "bg-yellow-50 border border-yellow-200"
            }`}
          >
            <div className="flex items-center gap-2">
              <Zap
                className={`h-5 w-5 ${
                  service.any_blocked ? "text-red-600" : "text-yellow-600"
                }`}
              />
              <div>
                <p
                  className={`text-sm font-medium ${
                    service.any_blocked ? "text-red-700" : "text-yellow-700"
                  }`}
                >
                  {service.any_blocked
                    ? "You've hit your usage limits"
                    : "You're approaching your usage limits"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Subscribe to a paid plan for higher limits and overage billing
                </p>
              </div>
            </div>
            <Button
              asChild
              size="sm"
              variant={service.any_blocked ? "destructive" : "default"}
            >
              <Link
                to="/portal/platform/subscriptions"
                search={{ upgrade: service.service_name }}
              >
                Upgrade
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {metricEntries.map(([metricKey, metric]) => {
            const metricInfo = METRIC_LABELS[metricKey] || {
              label: metricKey.replace(/_/g, " "),
              unit: "",
            };

            const progressColor =
              metric.status === "blocked"
                ? "bg-red-500"
                : metric.status === "overage"
                  ? "bg-amber-500"
                  : metric.status === "approaching"
                    ? "bg-yellow-500"
                    // WI16: "unknown" must not fall through to green — a
                    // reassuring bar for usage we could not measure is the same
                    // lie as showing 0. The bar is suppressed for unknowns
                    // below; this keeps it honest if that ever changes.
                    : metric.status === "unknown"
                      ? "bg-muted-foreground"
                      : "bg-green-500";

            // A metric with a 0 allowance is a pure per-usage (metered) product
            // — e.g. workflows billed per node. There's no bundle to exceed, so
            // it must read as metered usage, not a maxed-out "Overage".
            const isMetered = metric.limit <= 0;

            // WI16: the control plane could not measure this metric. `used`,
            // `remaining` and `percentage` are null — NOT 0. Rendering a 0 here
            // would recreate, in the UI, exactly the conflation the backend
            // change removed: the customer would read "no usage" when the truth
            // is "we don't know". Show it as unavailable and skip the bar.
            const isUnknown =
              metric.usage_unknown === true || metric.used === null;
            // Per-unit overage price in the org's currency, as the CP
            // resolved it (reporting-pricing W4). Absent for limit-only metrics.
            const overageRate = overageRateClause(
              metric.overage_rate,
              metric.currency,
              metricInfo.unit,
              { metered: isMetered },
            );
            return (
              <div key={metricKey} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{metricInfo.label}</span>
                  <div className="flex items-center gap-2">
                    {!isMetered && metric.status === "blocked" && (
                      <Badge variant="destructive" className="text-xs">
                        Blocked
                      </Badge>
                    )}
                    {!isMetered && metric.status === "overage" && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-amber-100 text-amber-700"
                      >
                        Overage
                      </Badge>
                    )}
                    {!isMetered && metric.status === "approaching" && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-yellow-100 text-yellow-700"
                      >
                        Approaching
                      </Badge>
                    )}
                    {isMetered && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-blue-100 text-blue-700"
                      >
                        Metered
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {isUnknown ? (
                        <>Unavailable</>
                      ) : isMetered ? (
                        <>
                          {metric.used?.toLocaleString()} {metricInfo.unit}
                        </>
                      ) : (
                        <>
                          {metric.used?.toLocaleString()} /{" "}
                          {metric.limit.toLocaleString()} {metricInfo.unit}
                        </>
                      )}
                      {overageRate && (
                        <span data-testid={`overage-rate-${metricKey}`}>
                          {" "}
                          · {overageRate}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {!isMetered && !isUnknown && (
                  <div className="relative">
                    <Progress
                      value={Math.min(metric.percentage ?? 0, 100)}
                      className="h-2"
                    />
                    <div
                      className={`absolute top-0 left-0 h-2 rounded-full ${progressColor}`}
                      style={{
                        width: `${Math.min(metric.percentage ?? 0, 100)}%`,
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isUnknown
                    ? "Usage could not be measured for this period — this has been logged for investigation. Your limit is not being enforced on it in the meantime."
                    : isMetered
                      ? `Metered usage — billed per ${metricInfo.unit} (no bundled allowance)`
                      : `${metric.remaining?.toLocaleString()} ${metricInfo.unit} remaining (${metric.percentage?.toFixed(1)}% used)`}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Service Usage Card Component - Now conditionally shows costs
interface ServiceUsageCardProps {
  serviceName: string;
  usage: ServiceUsageData;
  showCosts?: boolean;
}

function ServiceUsageCard({
  serviceName,
  usage,
  showCosts = false,
}: ServiceUsageCardProps) {
  const totalTokens = usage?.total_tokens || 0;
  const totalOperations =
    usage?.total_operations || usage?.operation_count || 0;
  const totalCost = parseFloat(String(usage?.total_cost_usd || "0"));
  const storageGib = parseFloat(String(usage?.storage_gib || "0"));
  const hasError = usage?.error;

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium">
            {SERVICE_NAMES[serviceName] || serviceName}
          </h4>
          {showCosts && (
            <p className="text-sm text-muted-foreground">
              {hasError ? "Error loading data" : `$${totalCost.toFixed(2)} USD`}
            </p>
          )}
        </div>
        {showCosts && (
          <Badge variant="outline">${totalCost.toFixed(2)}</Badge>
        )}
      </div>

      {!hasError && (
        <div className={`grid gap-4 text-sm ${showCosts ? "grid-cols-3" : "grid-cols-2"}`}>
          <div>
            <p className="text-muted-foreground">Operations</p>
            <p className="font-medium">
              {(totalOperations || totalTokens).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Storage</p>
            <p className="font-medium">{storageGib.toFixed(2)} GiB</p>
          </div>
          {showCosts && (
            <div>
              <p className="text-muted-foreground">Tokens</p>
              <p className="font-medium">{totalTokens.toLocaleString()}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
